// Guideline-review UI (task 4.7) — Req 5.5, 5.6, NFR6.3.
//
// The AI guideline-review skill flags notifiable / high-risk electrical work
// against a job: each flag carries a reason, whether it's notifiable under Part P,
// the likely certificate (EIC / MEIWC / EICR), a severity, advisory notes and a
// resolution the electrician can set. This module renders that as DECISION SUPPORT
// — every card leads with the disclaimer (Req 5.5 / NFR6.3) — and lets the
// electrician acknowledge or dismiss each flag, wired to the flags endpoint
// (Req 5.6):
//
//   GET /jobs/{id}/flags              list a job's stored flags
//   PUT /jobs/{id}/flags/{flagId}     set a flag's resolution (ack/dismissed)
//   POST /jobs/{id}/review            (re-)run the review against the job
//
// Two entry points:
//   • guidelineFlagsCard(review, { jobId }) — renders a review object as returned
//     inline on a quote draft (result.guidelineReview) or built from stored flags.
//     Ack/dismiss is only offered when the flag was persisted (has an id) against
//     a job — a draft raised without a job returns advisory-only flags.
//   • jobGuidelineSection(clientId, jobId) — self-loading section for the job
//     detail screen: lists stored flags and offers a Run/Re-run review button.

import { api } from '../api.js';
import {
  h, mount, runAsync,
  card, muted, sectionHeading, button, buttonRow, runAction, errorSlot,
  severityBadge, resolutionBadge, chip, disclaimerNote, DECISION_SUPPORT_DISCLAIMER,
} from './ui.js';

const enc = encodeURIComponent;

// Certificate precedence when summarising stored flags into one job-level
// certificate (mirrors api/lib/guidelineReview CERTIFICATE_PRECEDENCE).
const CERTIFICATE_PRECEDENCE = ['EIC', 'MEIWC', 'EICR'];

function pickCertificate(flags) {
  const named = new Set((flags || []).map((f) => f && f.certificate).filter(Boolean));
  return CERTIFICATE_PRECEDENCE.find((c) => named.has(c)) || null;
}

// One flag row: severity + notifiable + certificate chips, the reason, any
// advisory notes, the current resolution, and (when resolvable) ack/dismiss
// buttons. `onResolved(flag, resolution)` is called after a successful update so
// the caller can re-render with the new state.
function flagItem(flag, { jobId, onResolved }) {
  const chips = [severityBadge(flag.severity)];
  if (flag.notifiable) chips.push(chip('Notifiable · Part P', 'notifiable'));
  if (flag.certificate) chips.push(chip(flag.certificate));
  chips.push(resolutionBadge(flag.resolution));

  const err = errorSlot();
  const resolvable = !!(jobId && flag.id);

  const actions = [];
  if (resolvable) {
    const setResolution = (resolution, btn) => async () => {
      const updated = await runAction(
        btn,
        () => api.put(`/jobs/${enc(jobId)}/flags/${enc(flag.id)}`, { resolution }),
        { busyLabel: 'Saving…', errSlot: err },
      );
      if (updated) onResolved(flag, resolution);
    };
    if (flag.resolution !== 'ack') {
      const ackBtn = button('Acknowledge', { variant: 'primary', small: true });
      ackBtn.addEventListener('click', setResolution('ack', ackBtn));
      actions.push(ackBtn);
    }
    if (flag.resolution !== 'dismissed') {
      const dismissBtn = button('Dismiss', { small: true });
      dismissBtn.addEventListener('click', setResolution('dismissed', dismissBtn));
      actions.push(dismissBtn);
    }
    if (flag.resolution !== 'open') {
      const reopenBtn = button('Reopen', { small: true });
      reopenBtn.addEventListener('click', setResolution('open', reopenBtn));
      actions.push(reopenBtn);
    }
  }

  const notes = (flag.advisoryNotes || []).map((n) => h('li', { text: n }));

  return h('div', { class: `flag-item flag-${flag.severity || 'advisory'}` }, [
    h('div', { class: 'flag-chips' }, chips),
    flag.title ? h('p', { class: 'flag-title', text: flag.title }) : null,
    h('p', { class: 'flag-reason', text: flag.reason }),
    notes.length ? h('ul', { class: 'advisory-notes' }, notes) : null,
    actions.length ? buttonRow(actions) : null,
    err,
  ]);
}

// Render a review object as a card. `review` is either the guidelineReview payload
// from a quote draft or a synthesised one from stored flags. Re-renders itself in
// place when a flag resolution changes so the badges/buttons update immediately.
export function guidelineFlagsCard(review, { jobId } = {}) {
  const holder = h('div');

  const render = () => {
    if (!review) {
      mount(holder, null);
      return;
    }

    const parts = [disclaimerNote(review.disclaimer || DECISION_SUPPORT_DISCLAIMER)];

    if (review.reviewCompleted === false) {
      parts.push(muted(review.message || 'Guideline review was not completed for this work.'));
      mount(holder, card(parts, { title: 'Guideline review' }));
      return;
    }

    const flags = review.flags || [];
    const cert = review.requiredCertificate || pickCertificate(flags);
    if (cert) {
      parts.push(
        h('p', { class: 'cert-line' }, [
          h('span', { class: 'detail-label', text: 'Likely certificate: ' }),
          h('strong', { text: cert }),
        ]),
      );
    }

    if (!flags.length) {
      parts.push(muted('No notifiable or high-risk work was flagged.'));
    } else {
      const onResolved = (flag, resolution) => {
        flag.resolution = resolution;
        render();
      };
      parts.push(
        h('div', { class: 'flag-list' }, flags.map((f) => flagItem(f, { jobId, onResolved }))),
      );
    }

    if (Array.isArray(review.advisoryNotes) && review.advisoryNotes.length) {
      parts.push(sectionHeading('Advisory notes'));
      parts.push(h('ul', { class: 'advisory-notes' }, review.advisoryNotes.map((n) => h('li', { text: n }))));
    }
    if (review.notes) parts.push(muted(review.notes));

    mount(holder, card(parts, { title: 'Guideline review' }));
  };

  render();
  return holder;
}

// Self-loading guideline section for the job detail screen. Lists the job's stored
// flags (Req 5.6 — retained against the job) and offers a Run/Re-run review button
// that calls POST /jobs/{id}/review, then reloads. clientId is needed so the review
// can read the job's description and record the required certificate against it.
export function jobGuidelineSection(clientId, jobId) {
  const holder = h('div');

  const load = () =>
    runAsync(
      holder,
      () => api.get(`/jobs/${enc(jobId)}/flags`),
      (res) => renderSection((res && res.items) || []),
      { loadingMessage: 'Loading guideline review…' },
    );

  const renderSection = (flags) => {
    const err = errorSlot();
    const reviewBtn = button(flags.length ? 'Re-run guideline review' : 'Run guideline review', {
      variant: flags.length ? 'secondary' : 'primary',
    });
    reviewBtn.addEventListener('click', async () => {
      const out = await runAction(reviewBtn, () => api.post(`/jobs/${enc(jobId)}/review`, { clientId }), {
        busyLabel: 'Reviewing…', errSlot: err,
      });
      if (out) load();
    });

    if (!flags.length) {
      return h('div', {}, [
        card(
          [
            disclaimerNote(),
            muted('No guideline review recorded for this job yet. Run one to flag notifiable or high-risk work.'),
          ],
          { title: 'Guideline review' },
        ),
        buttonRow([reviewBtn]),
        err,
      ]);
    }

    const review = {
      reviewCompleted: true,
      disclaimer: DECISION_SUPPORT_DISCLAIMER,
      flags,
      requiredCertificate: pickCertificate(flags),
    };
    return h('div', {}, [guidelineFlagsCard(review, { jobId }), buttonRow([reviewBtn]), err]);
  };

  load();
  return holder;
}
